import { useEffect, useRef, useState, ComponentType, RefObject } from "react";
import RotateHandleIconImport from "./RotateHandleIcon";
import { calculateAngleFromMouse, normalizeAngle } from "./utils";

const RotateHandleIcon = (RotateHandleIconImport as unknown) as ComponentType<{
  size?: number;
}>;

interface InnerImageRotateHandleProps {
  rotation: number;
  onRotationPreview: (rotation: number) => void;
  onRotationCommit: (rotation: number) => void;
  disabled?: boolean;
  // Where on the rotating parent the handle's pivot point sits (in parent coords).
  // The handle attaches here and orbits as the parent rotates.
  anchorLeft?: number;
  anchorTop?: number;
  // How much to counter-rotate the icon so it stays upright on screen —
  // the rotating parent spins this component, so we spin the icon back by
  // the same amount.
  counterRotateDeg?: number;
  // The parent's flip scale (typically -1 or 1). The icon and badge live
  // inside the same flipped wrapper, so without countering the flip the
  // glyph would render mirrored.
  counterScaleX?: number;
  counterScaleY?: number;
  // Optional ref to a screen-space pivot element. Its bounding rect centre
  // is used as the rotation pivot. Provide one when the parent's bbox
  // doesn't coincide with the actual rotation pivot — e.g. when the image
  // sits at a non-zero offset inside the rotating wrap and so the wrap's
  // AABB centre drifts from the image centre under rotation.
  pivotRef?: RefObject<HTMLElement>;
}

const HANDLE_SIZE = 28;
const HANDLE_BELOW_OFFSET = 32;

const InnerImageRotateHandle = ({
  rotation,
  onRotationPreview,
  onRotationCommit,
  disabled = false,
  anchorLeft,
  anchorTop,
  counterRotateDeg = 0,
  counterScaleX = 1,
  counterScaleY = 1,
  pivotRef
}: InnerImageRotateHandleProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const initialRotationRef = useRef(0);
  const prevMouseAngleRef = useRef(0);
  const accumulatedDeltaRef = useRef(0);
  const lastRotationRef = useRef(rotation);
  // Bridge `disabled` into the document-level mousemove/mouseup listeners.
  // Those listeners are bound inside handleDragStart and would otherwise
  // capture the closure's `disabled` value (always false at bind time, since
  // handleDragStart early-returns when disabled), making any in-closure
  // `if (disabled) return` dead code. Reading from the ref means a parent
  // re-render with disabled=true is reflected on the next move/end tick.
  const disabledRef = useRef(disabled);
  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  // Rotation centre = centre of the supplied pivot element if one was given,
  // otherwise the rotating parent's own bbox. Callers pass `pivotRef` when
  // the parent's bbox doesn't coincide with the pivot — e.g. when the image
  // sits at a non-zero offset inside the wrap, the wrap's AABB centre drifts
  // from the image centre by R_θ(-offsetLeft, -offsetTop). A zero-size
  // element placed at the image centre inside the rotating wrap reports a
  // rect whose centre is invariant under the wrap's rotation around that
  // same point, so it tracks the true pivot regardless of θ.
  const getCenter = () => {
    const pivotEl = pivotRef?.current ?? wrapperRef.current?.parentElement;
    if (!pivotEl) return null;
    const rect = pivotEl.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
  };

  const getCoords = (e: MouseEvent | TouchEvent) => {
    if ("touches" in e) {
      const touch = e.touches[0];
      if (touch) return { x: touch.clientX, y: touch.clientY };
      return { x: 0, y: 0 };
    }
    return { x: e.clientX, y: e.clientY };
  };

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();

    const center = getCenter();
    if (!center) return;

    initialRotationRef.current = rotation;
    accumulatedDeltaRef.current = 0;

    let startCoords: { x: number; y: number };
    if ("touches" in e) {
      const touch = e.touches[0];
      if (!touch) return;
      startCoords = { x: touch.clientX, y: touch.clientY };
    } else {
      startCoords = { x: e.clientX, y: e.clientY };
    }

    prevMouseAngleRef.current = calculateAngleFromMouse(
      startCoords.x,
      startCoords.y,
      center.x,
      center.y
    );

    setIsDragging(true);

    const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
      // If the parent flips `disabled` to true mid-drag (selection change,
      // focus loss, programmatic state change), stop emitting previews so we
      // don't write rotations to a now-disabled element. Read via the ref
      // because the listener closes over the bind-time value.
      if (disabledRef.current) return;

      if ("touches" in moveEvent) moveEvent.preventDefault();

      const currentCenter = getCenter();
      if (!currentCenter) return;

      const coords = getCoords(moveEvent);
      const mouseAngle = calculateAngleFromMouse(
        coords.x,
        coords.y,
        currentCenter.x,
        currentCenter.y
      );

      let delta = mouseAngle - prevMouseAngleRef.current;
      if (delta > 180) delta -= 360;
      else if (delta < -180) delta += 360;

      accumulatedDeltaRef.current += delta;
      prevMouseAngleRef.current = mouseAngle;

      const nextRotation = normalizeAngle(
        initialRotationRef.current + accumulatedDeltaRef.current
      );
      lastRotationRef.current = nextRotation;
      onRotationPreview(nextRotation);
    };

    const handleEnd = (endEvent: MouseEvent | TouchEvent) => {
      endEvent.preventDefault();
      endEvent.stopPropagation();
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd, true);
      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleEnd);
      // Same disabled-mid-drag guard as handleMove: don't commit a rotation
      // change to an element that was disabled while the user was dragging.
      if (disabledRef.current) return;
      onRotationCommit(lastRotationRef.current);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd, true);
    document.addEventListener("touchmove", handleMove, { passive: false });
    document.addEventListener("touchend", handleEnd);

    // Suppress the click that otherwise fires after mouseup and deselects
    const suppressClick = (clickEvent: Event) => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
      document.removeEventListener("click", suppressClick, true);
    };
    document.addEventListener("click", suppressClick, true);
    setTimeout(
      () => document.removeEventListener("click", suppressClick, true),
      200
    );
  };

  // Position the handle beneath the image's bottom edge. If caller provides
  // anchor coords, use them (parent-coord pixel values). Otherwise fall back
  // to a bottom-centred offset on the parent's own bounds.
  const hasExplicitAnchor =
    typeof anchorLeft === "number" && typeof anchorTop === "number";

  const wrapperStyle: React.CSSProperties = hasExplicitAnchor
    ? {
        position: "absolute",
        left: `${anchorLeft}px`,
        top: `${(anchorTop as number) + HANDLE_BELOW_OFFSET}px`,
        width: `${HANDLE_SIZE}px`,
        height: `${HANDLE_SIZE}px`,
        marginLeft: `-${HANDLE_SIZE / 2}px`,
        zIndex: 20,
        pointerEvents: disabled ? "none" : "auto"
      }
    : {
        position: "absolute",
        left: "50%",
        top: `calc(100% + ${HANDLE_BELOW_OFFSET}px)`,
        transform: `translateX(-${HANDLE_SIZE / 2}px)`,
        width: `${HANDLE_SIZE}px`,
        height: `${HANDLE_SIZE}px`,
        zIndex: 20,
        pointerEvents: disabled ? "none" : "auto"
      };

  // The rotating parent applies `rotate(θ) scaleX(sx) scaleY(sy)`, which CSS
  // multiplies as M_parent = R(θ) · S_x · S_y (scales first, rotate last).
  // Its inverse is S_y⁻¹ · S_x⁻¹ · R(-θ), and since flips are self-inverse
  // (sx², sy² = 1) that's S_y · S_x · R(-θ), written as the CSS list
  // `scaleX(sx) scaleY(sy) rotate(-θ)` (scales BEFORE the rotate-undo). The
  // earlier order `rotate(-θ) scaleX(sx) scaleY(sy)` only matched when
  // either θ = 0 or both scales were 1; with e.g. sx = -1 and θ = 90° the
  // icon glyph ended up rotated 180° from where the user expected.
  const counterRotateStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    transform: `scaleX(${counterScaleX}) scaleY(${counterScaleY}) rotate(${-counterRotateDeg}deg)`,
    transformOrigin: "center"
  };

  const buttonStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    background: "#ffffff",
    border: "1px solid #d0d3d6",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: isDragging ? "grabbing" : "grab",
    touchAction: "none"
  };

  const badgeStyle: React.CSSProperties = {
    position: "absolute",
    top: "-22px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(0, 0, 0, 0.75)",
    color: "white",
    fontSize: "11px",
    padding: "2px 6px",
    borderRadius: "3px",
    whiteSpace: "nowrap",
    pointerEvents: "none"
  };

  // The parent DragSource (from react-dnd HTML5 backend) wraps this tree in
  // `draggable=true`, so any mousedown on a descendant starts a native HTML5
  // drag — which react-dnd interprets as "pan the image." We must opt the
  // handle's own subtree out of HTML5 drag so the mouse events reach our
  // rotation handler instead.
  const stopNativeDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      ref={wrapperRef}
      style={wrapperStyle}
      data-testid="inner-image-rotate-handle"
      draggable={false}
      onDragStart={stopNativeDrag}
    >
      <div style={counterRotateStyle}>
        <div
          style={buttonStyle}
          draggable={false}
          onDragStart={stopNativeDrag}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <RotateHandleIcon size={16} />
        </div>
        {isDragging && <div style={badgeStyle}>{Math.round(rotation)}°</div>}
      </div>
    </div>
  );
};

export default InnerImageRotateHandle;
